/**
 * Service worker registration
 *
 * Production-only: a stale worker in development is a debugging tax nobody
 * needs. Registration is deliberately quiet — the app must work identically
 * whether or not the worker is running.
 *
 * The update flow (prompting rather than reloading, FR-031) is wired in User
 * Story 6; this exposes the hook it will use.
 */

/**
 * Log what state the worker is actually in. `controlling: false` is the one
 * that matters — an uncontrolled page is served entirely by the network, so
 * offline fails with no error anywhere.
 */
const report = (moment, registration) => {
  const controlling = !!navigator.serviceWorker.controller;
  // eslint-disable-next-line no-console
  console.info(`Service worker [${moment}]:`, {
    installing: !!registration.installing,
    waiting: !!registration.waiting,
    active: !!registration.active,
    controlling,
    scope: registration.scope,
  });

  // An active worker that is not controlling this page intercepts nothing, so
  // offline does not work and nothing reports an error. The two ways to get
  // here both look like a working setup from the outside.
  if (moment === 'ready' && registration.active && !controlling) {
    console.warn(
      'Service worker is active but not controlling this page, so offline will not work here. ' +
      'This happens on the first load after install, and after a hard reload (Ctrl+Shift+R), ' +
      'which bypasses the worker by design. A normal reload (F5) will hand over control.',
    );
  }
};

/**
 * @param {{onUpdateAvailable?: (registration: ServiceWorkerRegistration) => void}} config
 */
export const register = (config = {}) => {
  // The NODE_ENV check already excludes the dev server, which is the case worth
  // excluding — a worker fighting hot reload. Localhost is deliberately NOT
  // excluded on top of that: service workers need a secure context, and
  // localhost is the only origin that qualifies without HTTPS, so it is the
  // only place a production build's offline behaviour can be verified locally.
  if (process.env.NODE_ENV !== 'production') {
    // Reached under `npm start`, whose bundle has no service worker in it at
    // all — CRA only generates one for a production build. Offline mode does
    // nothing here, and silently, which is worth saying out loud.
    // eslint-disable-next-line no-console
    console.info('Service worker disabled in development. Use `just run` to test offline mode.');
    return;
  }
  if (!('serviceWorker' in navigator)) return;

  const publicUrl = new URL(process.env.PUBLIC_URL || '', window.location.href);
  if (publicUrl.origin !== window.location.origin) return;

  // `load` may already have fired: this runs from a React effect, not from
  // module scope, and waiting for an event that has been and gone means never
  // registering at all.
  const start = () => {
    navigator.serviceWorker
      .register(`${process.env.PUBLIC_URL}/service-worker.js`)
      .then((registration) => {
        // A worker can be registered and running yet not controlling the page,
        // in which case it intercepts nothing and offline silently fails.
        //
        // Sampled at three moments on purpose: right after register() the
        // worker is still installing, so that reading alone says nothing.
        report('registered', registration);
        navigator.serviceWorker.ready.then((ready) => report('ready', ready));
        navigator.serviceWorker.addEventListener('controllerchange', () =>
          report('controllerchange', registration));

        // A worker that installed behind an older one waits indefinitely unless
        // something releases it. Offer the swap rather than forcing it: a silent
        // takeover can replace the shell mid-chapter (FR-031).
        if (registration.waiting && config.onUpdateAvailable) {
          config.onUpdateAvailable(registration);
        }

        registration.onupdatefound = () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.onstatechange = () => {
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              if (config.onUpdateAvailable) config.onUpdateAvailable(registration);
            }
          };
        };
      })
      .catch((error) => {
        console.warn('Service worker registration failed:', error);
      });
  };

  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
};

/** Activate a waiting worker and reload. Called only from a user action. */
export const applyUpdate = (registration) => {
  if (registration?.waiting) registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload());
};

export const unregister = () => {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.ready
    .then((registration) => registration.unregister())
    .catch((error) => console.warn(error.message));
};
